/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { CardContent } from '@/components/ui/card';
import { Upload, File, X } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  selectedFile: File | null;
}

export const FileUpload = ({ onFileSelect, selectedFile }: FileUploadProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const xlsFile = files.find((file) => file.name.endsWith('.xls') || file.name.endsWith('.xlsx'));

    if (xlsFile) {
      onFileSelect(xlsFile);
    } else {
      toast({
        title: 'Arquivo Inválido',
        description: 'Por favor, faça upload de um arquivo XLS ou XLSX',
        variant: 'destructive',
      });
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
        onFileSelect(file);
      } else {
        toast({
          title: 'Arquivo Inválido',
          description: 'Por favor, faça upload de um arquivo XLS ou XLSX',
          variant: 'destructive',
        });
      }
    }
  };

  const handleRemoveFile = () => {
    onFileSelect(null as any);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <CardContent className="pt-6">
      {!selectedFile ? (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
              border-2 border-dashed rounded-lg p-5 text-center transition-all
              ${
                isDragging
                  ? 'border-primary bg-primary/5 scale-[1.02]'
                  : 'border-border hover:border-primary/50'
              }
            `}
        >
          <Upload className="mx-auto h-10 w-10 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Upload de Arquivo XLS ou XLSX</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Arraste e solte seu arquivo aqui, ou clique para procurar
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileInput}
            className="hidden"
            id="file-upload"
          />
          <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
            Selecionar Arquivo
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between p-4 bg-secondary rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <File className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium">
                {selectedFile.name.length > 30
                  ? selectedFile.name.slice(0, 30) + '...'
                  : selectedFile.name}
              </p>
              <p className="text-sm text-muted-foreground">
                {(selectedFile.size / 1024).toFixed(2)} KB
              </p>
            </div>
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={handleRemoveFile}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </CardContent>
  );
};
